import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import "./VideoSection.css";
import { Link } from "react-router-dom";

const VideoPlayer = ({ videoSrc, onClose, isLoading }) => {
  const videoRef = useRef(null);

  if (!videoSrc && !isLoading) return null;

  return (
    <div className="video-player-container">
      {videoSrc && (
        <video 
          ref={videoRef}
          controls 
          autoPlay
          className="video-player" 
          src={videoSrc}
          onEnded={onClose}
        >
          Your browser does not support the video tag.
        </video>
      )}
      {isLoading && (
        <div className="video-loading-overlay">
          <div className="video-loading-spinner"></div>
        </div>
      )}
      <button className="close-button" onClick={onClose}>✖</button>
    </div>
  );
};

const VideoThumbnail = ({ thumbnailSrc, onClick, isLoading }) => {
  if (!thumbnailSrc) return null;

  return (
    <div className="thumbnail-container" onClick={onClick}>
      <img 
        src={thumbnailSrc} 
        alt="Video Thumbnail" 
        className="thumbnail-image"
      />
      {isLoading ? (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
        </div>
      ) : (
        <div className="play-overlay">▶</div>
      )}
    </div>
  );
};

const VideoSection = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentVideo, setCurrentVideo] = useState(null);
  const [currentVideoPage, setCurrentVideoPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isVideoLoading, setIsVideoLoading] = useState(false);
  const [loadingVideoId, setLoadingVideoId] = useState(null);
  const [videoError, setVideoError] = useState(null);

  const fetchVideos = async (videoPage) => {
    setLoading(true);
    try {
      const response = await axios.get(
        `https://khanehyeghtesad.com:8000/api/video/${videoPage}?page=1&limit=5`
      );
      
      if (response.data && Array.isArray(response.data.articles)) {
        setVideos((prevVideos) => {
          return videoPage === 1 
            ? response.data.articles 
            : [...prevVideos, ...response.data.articles];
        });
  
        // Use the total_pages from API to decide if there's more content
        setHasMore(videoPage < response.data.total_pages);
      } else {
        throw new Error("Invalid video data received from API.");
      }
    } catch (err) {
      setError("Failed to fetch videos. Please try again later.");
    }
    setLoading(false);
  };
  

  useEffect(() => {
    fetchVideos(1);
  }, []);

  const loadMore = () => {
    const nextVideoPage = currentVideoPage + 1;
    setCurrentVideoPage(nextVideoPage);
    fetchVideos(nextVideoPage);
  };

  const playVideo = async (videoId) => {
    // If it's the same video that's currently playing, do nothing
    if (currentVideo && currentVideo._id === videoId) return;

    // Reset current video and show loading state
    setCurrentVideo(null);
    setIsVideoLoading(true);
    setLoadingVideoId(videoId);
    setVideoError(null);
    
    try {
      const response = await axios.get(
        `https://khanehyeghtesad.com:8000/api/video/article/${videoId}`
      );
      
      if (response.data?.video_file) {
        setCurrentVideo(response.data);
      } else {
        throw new Error("No video file in response");
      }
    } catch (err) {
      setVideoError("Failed to play the video. Please try again later.");
      console.error("Error playing video:", err);
    } finally {
      setIsVideoLoading(false);
      setLoadingVideoId(null);
    }
  };

  const stopVideo = () => {
    setCurrentVideo(null);
    setVideoError(null);
    setIsVideoLoading(false);
    setLoadingVideoId(null);
  };

  if (!videos.length && loading) 
    return <div className="video-section-loading">Loading...</div>;
  if (!videos.length) 
    return <div className="video-section-no-videos">No videos available</div>;

  return (
    <section id="videos" className="video-section">
      <h2 className="video-section-title">ویدیوها</h2>
      <div className="video-section-container">
        <div className="video-large">
          <div className="video-wrapper">
            {currentVideo && currentVideo._id === videos[0]._id ? (
              <VideoPlayer
                videoSrc={currentVideo.video_file}
                onClose={stopVideo}
                isLoading={isVideoLoading && loadingVideoId === videos[0]._id}
              />
            ) : (
              <VideoThumbnail
                thumbnailSrc={`data:image/jpeg;base64,${videos[0].thumbnail}`}
                onClick={() => playVideo(videos[0]._id)}
                isLoading={isVideoLoading && loadingVideoId === videos[0]._id}
              />
            )}
          </div>
          <Link to={`/article/${videos[0].slug}`} className="video-wrapper-link">
            <p className="news-quote-latest">{videos[0].quote}</p>
            <h3 className="video-title">{videos[0].title}</h3>
          </Link>
        </div>

        <div className="small-videos-container">
          <div className="video-small-container">
            {videos.slice(1).map((video) => (
              <div
                key={video._id}
                className={`video-small ${currentVideo?._id === video._id ? "active-video" : ""}`}
              >
                <div className="video-wrapper-small">
                  {currentVideo && currentVideo._id === video._id ? (
                    <VideoPlayer
                      videoSrc={currentVideo.video_file}
                      onClose={stopVideo}
                      isLoading={isVideoLoading && loadingVideoId === video._id}
                    />
                  ) : (
                    <VideoThumbnail
                      thumbnailSrc={`data:image/jpeg;base64,${video.thumbnail}`}
                      onClick={() => playVideo(video._id)}
                      isLoading={isVideoLoading && loadingVideoId === video._id}
                    />
                  )}
                </div>
                <Link to={`/article/${video.slug}`} className="video-wrapper-link">
                  <h4 className="video-small-title">{video.title}</h4>
                </Link>
              </div>
            ))}
          </div>
          {hasMore && (
            <button 
              className="load-more-button" 
              onClick={loadMore} 
              disabled={loading}
            >
              {loading ? '...' : 'بارگذاری بیشتر'}
            </button>
          )}
        </div>
      </div>
    </section>
  );
};

export default VideoSection;